import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SuperUserAuthProvider, useSuperUserAuth } from './contexts/SuperUserAuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CapabilitiesProvider } from './contexts/CapabilitiesContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import ProfilePage from './pages/ProfilePage';
import SuperUserLogin from './pages/superuser/SuperUserLogin';
import SuperUserDashboard from './pages/superuser/SuperUserDashboard';
import SupportEntry from './pages/SupportEntry';
import { getDeploymentInfo } from './services/deployment';
import SmartWatchSetup from './pages/SmartWatchSetup';
// New Advanced Features
import GamificationPage from './pages/GamificationPage';
import CheckInPage from './pages/CheckInPage';
import BuddyPage from './pages/BuddyPage';
import CreditsPage from './pages/CreditsPage';
import NotificationsPage from './pages/NotificationsPage';
import MessagesPage from './pages/MessagesPage';
import OnlineClassesPage from './pages/OnlineClassesPage';
import AdminAdvancedDashboard from './pages/AdminAdvancedDashboard';
import AdminAnalyticsProPage from './pages/AdminAnalyticsProPage';
import SettingsPageFull from './components/SettingsPageFull';
import SettingsPage from './components/SettingsPage';
import { DashboardLayout } from './components/Layout';
import ClientLayout from './components/Layout/ClientLayout';
import FeatureRoute from './components/FeatureRoute';
import TrainingHubPage from './pages/TrainingHubPage';
import { legacyTabRedirect } from './utils/legacyRoutes';
import ReportsExport from './components/ReportsExport';
import GoogleCalendarConnect from './components/GoogleCalendarConnect';
import CreditShop from './components/CreditShop';
import AIPersonalTrainer from './components/AIPersonalTrainer';
import AdminAIDashboard from './components/AdminAIDashboard';
import MealPlanManagement from './components/MealPlanManagement';
import UserManagement from './components/UserManagement';
import SupplierManagement from './components/SupplierManagement';
import RoleManagement from './components/RoleManagement';
import TokenShop from './components/TokenShop';
import AdminBillingPage from './pages/AdminBillingPage';
import AdminTokenShopPage from './pages/AdminTokenShopPage';
// AdminWarehousePage and AdminPurchasesPage removed - integrated into AdminBillingPage
import ClientBillingPage from './pages/ClientBillingPage';
import AdminFinancialConsultantPage from './pages/AdminFinancialConsultantPage';
import PurchasesList from './pages/PurchasesList';
import PurchaseInvoiceForm from './pages/PurchaseInvoiceForm';
import SalesList from './pages/SalesList';
import SaleInvoiceForm from './pages/SaleInvoiceForm';
import AdminWorkoutPlanStudioPage from './pages/AdminWorkoutPlanStudioPage';
import AdminExerciseTaxonomyPage from './pages/AdminExerciseTaxonomyPage';
import AdminExerciseLibraryPage from './pages/AdminExerciseLibraryPage';
import AdminErpPage from './pages/AdminErpPage';
import OperationsDashboardPage from './pages/OperationsDashboardPage';
import ReceptionPage from './pages/ReceptionPage';
import Member360Page from './pages/Member360Page';
import RetentionDashboardPage from './pages/RetentionDashboardPage';
import AdminInactiveMembersPage from './pages/AdminInactiveMembersPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import PtDashboardPage from './pages/PtDashboardPage';
import ClientHomePage from './pages/ClientHomePage';
import ClientPtPage from './pages/ClientPtPage';
import WorkoutPlayerPage from './pages/WorkoutPlayerPage';
import ClientProgressPage from './pages/ClientProgressPage';
import HuaweiCallbackPage from './pages/HuaweiCallbackPage';
import { ForcedPasswordChange } from './components/ChangePasswordForm';
import 'react-toastify/dist/ReactToastify.css';
import './styles/theme.css';
import './App.css';

const PrivateRoute = ({ children, adminOnly = false, permission = null }) => {
  const { user, isAdmin, loading, hasPermission, permissionsLoading } = useAuth();

  // Uma rota que depende de uma permissão espera pelas permissões do servidor (um Admin não precisa delas).
  if (loading || (permission && user && !isAdmin && permissionsLoading)) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" />;
  }

  // Verificar permissão de funcionalidade (só para clientes)
  if (permission && !isAdmin && !hasPermission(permission)) {
    return <Navigate to="/" />;
  }

  return children;
};

// "/" é o Início. Os endereços antigos do portal (/?tab=booking, /?tab=workout…) levam ao sítio novo.
const ClientHomeRoute = () => {
  const { search } = useLocation();
  const target = legacyTabRedirect(search);
  if (target !== null) return <Navigate to={target} replace />;
  return <ClientHomePage />;
};

// Legacy route redirects (keep admin navigation under /admin)
const LegacyPurchasesRedirect = () => <Navigate to="/admin/purchases" replace />;
const LegacyPurchasesNewRedirect = () => <Navigate to="/admin/purchases/new" replace />;
const LegacyPurchasesIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/admin/purchases/${id}`} replace />;
};

const LegacySalesRedirect = () => <Navigate to="/admin/sales" replace />;
const LegacySalesNewRedirect = () => <Navigate to="/admin/sales/new" replace />;
const LegacySalesIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/admin/sales/${id}`} replace />;
};

const SuperUserPrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSuperUserAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/superuser/login" />;
  }

  return children;
};

// O que esta implantação é, vindo do servidor: o portal de plataforma só tem a consola; um ginásio não tem consola.
function useDeploymentInfo() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    getDeploymentInfo().then((i) => { if (alive) setInfo(i); });
    return () => { alive = false; };
  }, []);
  return info;
}

// A consola de plataforma vive no portal, noutro endereço: quem abre /superuser aqui é levado para lá.
function PlatformRedirect({ url }) {
  useEffect(() => { window.location.replace(`${url}/superuser/login`); }, [url]);
  return <div className="loading"><div className="spinner"></div></div>;
}

function AppContent() {
  const info = useDeploymentInfo();
  const mode = info ? info.mode : null;
  const platformUrl = info ? info.platformUrl : '';
  const { user: sessionUser } = useAuth();

  if (mode === null) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  // Entrou com uma password temporária (definida pelo administrador): antes de qualquer outra coisa, escolhe uma sua.
  // O servidor recusa tudo o resto enquanto o token for o da password temporária; isto é só o ecrã.
  if (mode !== 'Platform' && sessionUser?.mustChangePassword) {
    return <div className="app"><ForcedPasswordChange /></div>;
  }

  // PORTAL DE PLATAFORMA: a consola que gere todas as instâncias. Não há ginásio aqui (nem portal do sócio, nem registo).
  if (mode === 'Platform') {
    return (
      <Router>
        <div className="app">
          <Routes>
            <Route path="/superuser/login" element={<SuperUserLogin />} />
            <Route path="/superadmin/login" element={<Navigate to="/superuser/login" replace />} />
            <Route path="/superadmin" element={<Navigate to="/superuser" replace />} />
            <Route path="/superuser" element={
              <SuperUserPrivateRoute>
                <SuperUserDashboard />
              </SuperUserPrivateRoute>
            } />
            <Route path="*" element={<Navigate to="/superuser" replace />} />
          </Routes>
        </div>
      </Router>
    );
  }

  // Com o portal noutro endereço (platformUrl), este ginásio não tem consola: nem a combinada.
  const hasConsole = mode !== 'Gym' && !platformUrl;

  return (
    <Router>
      <div className="app">
        <Routes>
          {/* Consola da plataforma: só na implantação combinada. Num ginásio estes endereços não existem. */}
          {hasConsole && <Route path="/superuser/login" element={<SuperUserLogin />} />}
          {hasConsole && <Route path="/superadmin/login" element={<Navigate to="/superuser/login" replace />} />}
          {hasConsole && <Route path="/superadmin" element={<Navigate to="/superuser" replace />} />}
          {hasConsole && (
            <Route path="/superuser" element={
              <SuperUserPrivateRoute>
                <SuperUserDashboard />
              </SuperUserPrivateRoute>
            } />
          )}
          {!hasConsole && platformUrl && <Route path="/superuser/*" element={<PlatformRedirect url={platformUrl} />} />}
          {!hasConsole && platformUrl && <Route path="/superadmin/*" element={<PlatformRedirect url={platformUrl} />} />}
          {!hasConsole && !platformUrl && <Route path="/superuser/*" element={<Navigate to="/" replace />} />}
          {!hasConsole && !platformUrl && <Route path="/superadmin/*" element={<Navigate to="/" replace />} />}

          {/* Chegada de um acesso de suporte aberto no portal de plataforma (token no fragmento do URL). */}
          <Route path="/suporte" element={<SupportEntry />} />
          
          {/* Main App Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* O player de treino do sócio ("O meu treino de hoje"): ecrã próprio, sem o menu do painel. */}
          <Route path="/treino" element={
            <PrivateRoute permission="canAccessWorkoutPlans">
              <FeatureRoute feature="TrainingPlans">
                <WorkoutPlayerPage />
              </FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/workout" element={<Navigate to="/treino" replace />} />
          <Route path="/fitness/huawei/callback" element={
            <PrivateRoute>
              <HuaweiCallbackPage />
            </PrivateRoute>
          } />

          {/*
            A área do sócio: o menu lateral e o cabeçalho abrem UMA vez (ClientLayout) e as páginas trocam lá dentro.
            Cada página de um módulo só existe se o ginásio o tiver ligado (FeatureRoute); a permissão do perfil continua a mandar (PrivateRoute).
            Os endereços antigos (/home, /?tab=…, /progress…) continuam a funcionar e levam ao sítio novo.
          */}
          <Route element={<PrivateRoute><ClientLayout /></PrivateRoute>}>
            <Route path="/" element={<ClientHomeRoute />} />
            <Route path="/home" element={<Navigate to="/" replace />} />

            {/* Reservas */}
            <Route path="/reservas" element={<PrivateRoute permission="canAccessBookings"><FeatureRoute feature="Reservations"><ClientDashboard tab="booking" /></FeatureRoute></PrivateRoute>} />
            <Route path="/reservas/minhas" element={<PrivateRoute permission="canAccessBookings"><FeatureRoute feature="Reservations"><ClientDashboard tab="mybookings" /></FeatureRoute></PrivateRoute>} />
            <Route path="/classes" element={<PrivateRoute permission="canAccessClasses"><FeatureRoute feature="OnlineClasses"><OnlineClassesPage /></FeatureRoute></PrivateRoute>} />

            {/* Treino */}
            <Route path="/meu-treino" element={<PrivateRoute permission="canAccessWorkoutPlans"><FeatureRoute feature="TrainingPlans"><TrainingHubPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/meu-treino/plano" element={<PrivateRoute permission="canAccessWorkoutPlans"><FeatureRoute feature="TrainingPlans"><TrainingHubPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/meu-treino/historico" element={<PrivateRoute permission="canAccessWorkoutPlans"><FeatureRoute feature="TrainingPlans"><TrainingHubPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/meu-treino/exercicios" element={<PrivateRoute permission="canAccessWorkoutPlans"><FeatureRoute feature="TrainingPlans"><TrainingHubPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/pt/sessions" element={<FeatureRoute feature="PersonalTraining"><ClientPtPage /></FeatureRoute>} />
            <Route path="/ai-trainer" element={<PrivateRoute permission="canAccessAITrainer"><FeatureRoute feature="AiTrainer"><AIPersonalTrainer /></FeatureRoute></PrivateRoute>} />
            <Route path="/meal-plan" element={<PrivateRoute permission="canAccessMealPlans"><FeatureRoute feature="MealPlans"><MealPlanManagement /></FeatureRoute></PrivateRoute>} />

            {/* Evolução */}
            <Route path="/progress" element={<FeatureRoute anyOf={['TrainingPlans', 'ClientProgress']}><ClientProgressPage /></FeatureRoute>} />
            <Route path="/gamification" element={<PrivateRoute permission="canAccessGamification"><FeatureRoute feature="Gamification"><GamificationPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/smartwatch" element={<PrivateRoute permission="canAccessSmartwatch"><FeatureRoute feature="Wearables"><SmartWatchSetup /></FeatureRoute></PrivateRoute>} />
            <Route path="/checkin" element={<CheckInPage />} />
            <Route path="/buddy" element={<BuddyPage />} />

            {/* Conta */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/billing" element={<PrivateRoute permission="canAccessCredits"><FeatureRoute feature="Billing"><ClientBillingPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/credits" element={<PrivateRoute permission="canAccessCredits"><CreditsPage /></PrivateRoute>} />
            <Route path="/token-shop" element={<PrivateRoute permission="canAccessTokenShop"><TokenShop /></PrivateRoute>} />
            <Route path="/messages" element={<PrivateRoute permission="canAccessMessages"><FeatureRoute feature="Messaging"><MessagesPage /></FeatureRoute></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute permission="canAccessNotifications"><FeatureRoute feature="Notifications"><NotificationsPage /></FeatureRoute></PrivateRoute>} />

            {/* Integration Pages */}
            <Route path="/reports/export" element={<ReportsExport />} />
            <Route path="/calendar/connect" element={<GoogleCalendarConnect />} />
            <Route path="/shop" element={<CreditShop />} />
          </Route>

          {/* Aliases (legacy URLs) */}
          <Route path="/analytics" element={
            <PrivateRoute adminOnly>
              <AdminAdvancedDashboard />
            </PrivateRoute>
          } />
          <Route path="/analytics-pro" element={
            <PrivateRoute adminOnly>
              <AdminAnalyticsProPage />
            </PrivateRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <PrivateRoute adminOnly>
              <AdminAdvancedDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/legacy-dashboard" element={
            <PrivateRoute adminOnly>
              <AdminDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/operations" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <OperationsDashboardPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/reception" element={
            <PrivateRoute adminOnly><FeatureRoute feature="Reception">
              <DashboardLayout userType="admin">
                <ReceptionPage />
              </DashboardLayout></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/members/:id/360" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <Member360Page />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/inactive-members" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <AdminInactiveMembersPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          {/* Pedidos de suporte ao operador da plataforma: o registo do que o ginásio pediu.
              (A «sessão de suporte» é outra coisa: o operador a entrar neste ginásio.) */}
          <Route path="/admin/support" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <SupportTicketsPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/retention" element={
            <PrivateRoute adminOnly><FeatureRoute feature="Retention">
              <DashboardLayout userType="admin">
                <RetentionDashboardPage />
              </DashboardLayout></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/pt" element={
            <PrivateRoute adminOnly><FeatureRoute feature="PersonalTraining">
              <DashboardLayout userType="admin">
                <PtDashboardPage />
              </DashboardLayout></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/analytics" element={
            <PrivateRoute adminOnly>
              <AdminAdvancedDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/analytics-pro" element={
            <PrivateRoute adminOnly>
              <AdminAnalyticsProPage />
            </PrivateRoute>
          } />
          <Route path="/admin/gamification" element={
            <PrivateRoute adminOnly><FeatureRoute feature="Gamification">
              <GamificationPage userType="admin" /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/ai" element={
            <PrivateRoute adminOnly><FeatureRoute feature="AiTrainer">
              <AdminAIDashboard /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/ai-trainer" element={
            <PrivateRoute adminOnly><FeatureRoute feature="AiTrainer">
              <AdminAIDashboard /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/ai-dashboard" element={
            <PrivateRoute adminOnly><FeatureRoute feature="AiTrainer">
              <AdminAIDashboard /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/meal-plans" element={
            <PrivateRoute adminOnly><FeatureRoute feature="MealPlans">
              <MealPlanManagement isAdmin={true} /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/users" element={
            <PrivateRoute adminOnly>
              <UserManagement />
            </PrivateRoute>
          } />
          <Route path="/users" element={
            <PrivateRoute adminOnly>
              <UserManagement />
            </PrivateRoute>
          } />
          <Route path="/admin/roles" element={
            <PrivateRoute adminOnly>
              <RoleManagement />
            </PrivateRoute>
          } />
          <Route path="/admin/settings" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <SettingsPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/settings-basic" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <SettingsPageFull />
              </DashboardLayout>
            </PrivateRoute>
          } />

          <Route path="/admin/consultor-financeiro" element={
            <PrivateRoute adminOnly>
              <AdminFinancialConsultantPage />
            </PrivateRoute>
          } />
          <Route path="/admin/billing" element={
            <PrivateRoute adminOnly><FeatureRoute feature="Billing">
              <AdminBillingPage /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/billing/:section" element={
            <PrivateRoute adminOnly><FeatureRoute feature="Billing">
              <AdminBillingPage /></FeatureRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/suppliers" element={
            <PrivateRoute adminOnly>
              <SupplierManagement />
            </PrivateRoute>
          } />
          <Route path="/admin/token-shop" element={
            <PrivateRoute adminOnly>
              <AdminTokenShopPage />
            </PrivateRoute>
          } />
          <Route path="/admin/workout-plans-studio" element={
            <PrivateRoute permission="canManageWorkoutPlans"><FeatureRoute feature="TrainingPlans">
              <DashboardLayout userType="admin">
                <AdminWorkoutPlanStudioPage />
              </DashboardLayout></FeatureRoute>
            </PrivateRoute>
          } />

          <Route path="/admin/exercise-taxonomy" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <AdminExerciseTaxonomyPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          {/* Biblioteca de exercícios e equipamento: um Admin, ou um perfil com "Gerir Biblioteca". */}
          <Route path="/admin/exercise-library" element={
            <PrivateRoute permission="canManageExerciseLibrary">
              <DashboardLayout userType="admin">
                <AdminExerciseLibraryPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/erp" element={
            <PrivateRoute adminOnly>
              <AdminErpPage />
            </PrivateRoute>
          } />

          {/* Purchases Routes (Admin) */}
          <Route path="/admin/purchases" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <PurchasesList />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/purchases/new" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <PurchaseInvoiceForm />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/purchases/:id" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <PurchaseInvoiceForm />
              </DashboardLayout>
            </PrivateRoute>
          } />

          {/* Sales Routes (Admin) */}
          <Route path="/admin/sales" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <SalesList />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/sales/new" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <SaleInvoiceForm />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/admin/sales/:id" element={
            <PrivateRoute adminOnly>
              <DashboardLayout userType="admin">
                <SaleInvoiceForm />
              </DashboardLayout>
            </PrivateRoute>
          } />

          {/* Legacy routes (redirect to admin area) */}
          <Route path="/purchases" element={<LegacyPurchasesRedirect />} />
          <Route path="/purchases/new" element={<LegacyPurchasesNewRedirect />} />
          <Route path="/purchases/:id" element={<LegacyPurchasesIdRedirect />} />
          <Route path="/sales" element={<LegacySalesRedirect />} />
          <Route path="/sales/new" element={<LegacySalesNewRedirect />} />
          <Route path="/sales/:id" element={<LegacySalesIdRedirect />} />
          {/* Warehouse & Purchases integrated into AdminBillingPage tabs */}
          
        </Routes>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <CapabilitiesProvider>
            <SuperUserAuthProvider>
              <AppContent />
            </SuperUserAuthProvider>
          </CapabilitiesProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
